import {ReloadOutlined, SearchOutlined} from '@ant-design/icons/lib';
import {Button} from 'antd';
import {Interval, useStoresQuery} from 'api/generated';
import {SearchBar} from 'components';
import {FilterInput} from 'components/FilterInput';
import {Formik} from 'formik';
import {DatePicker, Form, Select} from 'formik-antd';
import moment, {Moment} from 'moment';
import React, {useState} from 'react';
import styled from 'styled-components';
import {LabelValue} from 'types';
import {useAccessLevel} from '../../../hooks/useAccessLevel';
import {ExtraFilter} from './types';

const extraFilters: ExtraFilter[] = ['paymentMethodCardBrand'];

type FilterValues = {
  filterName: string;
  filterValue: string;
  createdAt: [Moment, Moment];
} & Partial<Record<ExtraFilter, string>>;

export type FilterQueryParams = {
  createdAt?: string[];
  [key: string]: any;
};

type Props = {
  onChange: (values: FilterQueryParams) => void;
  queryFilters: FilterQueryParams;
  accountId: string;
};

const RangePicker = styled(DatePicker.RangePicker)`
  min-width: 240px;
`;

export const SubscriptionsFilters: React.FC<Props> = ({onChange, queryFilters, accountId}) => {
  const {createdAt = [], ...filters} = queryFilters;
  const {isAdmin} = useAccessLevel();
  const {data} = useStoresQuery({skip: !isAdmin, variables: {accountId}});
  const stores = data?.stores.items.map((store) => ({
    label: store.name as string,
    value: store.id
  }));

  const dropdownFilters = {
    customerEmail: 'Customer email',
    customerName: 'Customer name',
    customerPhone: 'Customer phone number',
    id: 'Subscription ID',
    interval: 'Interval',
    paymentMethodCardLast4: 'Card last 4 digits',
    sequenceId: 'Sequence ID',
    lastPaymentId: 'Last payment ID',
    lastOrderId: 'Last order ID',
    ...(stores?.length ? {storeId: 'Store'} : {})
  };
  const intervalOptions = [
    {label: 'hour', value: Interval.HOUR},
    {label: 'day', value: Interval.DAY},
    {label: 'week', value: Interval.WEEK},
    {label: 'month', value: Interval.MONTH},
    {label: 'quarter', value: Interval.QUARTER},
    {label: 'year', value: Interval.QUARTER}
  ];
  const selectedOptions: Record<string, any> = {
    interval: intervalOptions
  };
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<LabelValue[]>([]);

  const defaultFilterName = 'customerEmail';
  const availableFilters = dropdownFilters;
  const filterName =
    Object.keys(filters)
      .filter((key) => !extraFilters.includes(key as ExtraFilter))
      .find((key) => filters[key]) || defaultFilterName;

  const initialValues: FilterValues = {
    filterName: filterName,
    filterValue: filters[filterName] || '',
    createdAt: [moment(createdAt[0]).startOf('day'), moment(createdAt[1]).endOf('day')]
  };
  Object.values(extraFilters).forEach((val) => {
    initialValues[val as ExtraFilter] = filters[val] || undefined;
  });

  const handleSubmit = ({createdAt, filterName, filterValue, ...extra}: FilterValues) => {
    const [startMoment, endMoment] = createdAt;
    const start = startMoment ? startMoment : moment().subtract(1, 'day');
    const end = endMoment ? endMoment : moment();
    const filter: FilterQueryParams = {...filters};

    Object.keys(filter).forEach((key) => {
      (filter as any)[key] = key === filterName ? filterValue : undefined;
    });
    filter.createdAt = [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
    return onChange(filter);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({dirty, submitForm, values}) => (
        <Form>
          <SearchBar compact>
            <Select
              name="filterName"
              style={{minWidth: 250}}
              showSearch
              optionFilterProp="children"
              listHeight={500}
              onChange={(value) => {
                setSelectedFilterOptions(selectedOptions[value] || []);
              }}>
              {Object.entries(availableFilters).map(([value, label]) => {
                return (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                );
              })}
            </Select>
            <FilterInput
              filterName={values.filterName}
              filterValue={values.filterValue}
              filterOptions={selectedFilterOptions}
            />
            <RangePicker
              name="createdAt"
              format="L"
              allowClear={false}
              onOpenChange={(isOpen) => !isOpen && submitForm()}
              disabledDate={(date) => (date ? date.isAfter(moment().endOf('day')) : false)}
              ranges={{
                Today: [moment().startOf('day'), moment().endOf('day')],
                'Last week': [moment().startOf('day').subtract(7, 'days'), moment().endOf('day')],
                'Last 14 days': [
                  moment().startOf('day').subtract(14, 'days'),
                  moment().endOf('day')
                ],
                'Last month': [moment().startOf('day').subtract(1, 'month'), moment().endOf('day')],
                'Last 3 months': [
                  moment().startOf('day').subtract(3, 'month'),
                  moment().endOf('day')
                ]
              }}
            />
            <Button htmlType="submit" style={{minWidth: 130}} type={dirty ? 'primary' : undefined}>
              {dirty ? (
                <>
                  <SearchOutlined /> Search
                </>
              ) : (
                <>
                  <ReloadOutlined /> Refresh
                </>
              )}
            </Button>
          </SearchBar>
        </Form>
      )}
    </Formik>
  );
};
